import { computed, inject, Injector, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { DescriptiveSelectItem } from "@eforall/dashboard";
import { AccApplicationStatusId } from "@interfaces";
import { DomainDataService } from "../../../../../services";
import { AccApplicationDialogDataService } from "../../acc-application-dialog-data.service";


export class EditStatusLogFormService {

	public readonly form = new CommonForm<{ statusId: number, note: string }>(inject(Injector));
	private domain = inject(DomainDataService);
	public accAppDialogDataService = inject(AccApplicationDialogDataService);
	private readonly fromAccApplicationStatusId = computed(() => this.accAppDialogDataService.dialogData()?.details.accApplicationStatusId);
	private readonly logNotes = computed(() => {
		const toStatusId = this.toAccApplicationStatusId().pendingValue() || this.toAccApplicationStatusId().actualValue();

		if (this.fromAccApplicationStatusId() == toStatusId) {
			return this.accAppDialogDataService.dialogData()?.statusLogs
				.sort((a, b) => b.updatedUTC - a.updatedUTC)
				.find(status => status.toAccApplicationStatusId == this.fromAccApplicationStatusId())
				?.notes || '';
		}
		else return '';
	});


	public deferValue = signal<boolean>(this.accAppDialogDataService.dialogData()?.details.isDeferred ?? false);

	public deferCheckboxDisabled = computed(() => {
		if (this.accAppDialogDataService.isReadonly()) return true;
		const toStatusId = this.toAccApplicationStatusId().pendingValue() || this.toAccApplicationStatusId().actualValue();

		const closedStatuses = this.domain.data().accApplicationStatuses.filter(status => !status.isOpen).map(status => status.accApplicationStatusId);
		if (!closedStatuses.includes(toStatusId)) return true;
		return false;
	});

	public deferApplication = computed(() => this.deferValue());


	constructor() {

		toObservable(this.accAppDialogDataService.dialogData).subscribe(dialogData => {
			this.deferValue.set(dialogData?.details.isDeferred ?? false);
		});

		toObservable(this.toAccApplicationStatusId().pendingValue).subscribe(pendingToAccApplicationStatusId => {
			const closedStatuses = this.domain.data().accApplicationStatuses.filter(status => !status.isOpen).map(status => status.accApplicationStatusId);
			if (pendingToAccApplicationStatusId && !closedStatuses.includes(pendingToAccApplicationStatusId)) {
				this.deferValue.set(this.accAppDialogDataService.dialogData()?.details.isDeferred ?? false);
			}

			this.note().pendingValue.set(this.logNotes() ?? null);
		});

	}

	/**
	 * Get the appropriate FontAwesome icon class based on status ID
	 * Matches the logic from StatusLogListPart
	 */
	private getStatusIcon(statusId: number): string {
		if (statusId == 1) return 'fa-fw fa-circle-info fa-solid text-secondary';
		else if (statusId == 3 || statusId == 4 || statusId == 5) return 'fa-fw fa-circle-xmark fa-solid text-danger';
		else if (statusId == 9) return 'fa-fw fa-circle-check fa-solid text-success';
		else return 'fa-fw fa-circle-info fa-solid text-info';
	}

	/**
	 * Extract color from icon class text-* utility classes
	 */
	private getIconColor(iconClass: string): string {
		if (iconClass.includes('text-secondary')) return '#6c757d';
		else if (iconClass.includes('text-danger')) return '#dc3545';
		else if (iconClass.includes('text-success')) return '#28a745';
		else if (iconClass.includes('text-info')) return '#17a2b8';
		else return '#4CAF50'; // default green
	}

	private readonly statusOptions = computed(() => {
		const domainData = this.domain.data();
		return domainData.accApplicationStatuses.map(status => ({
			text: status.name,
			subText: status.desc,
			value: status.accApplicationStatusId,
			disabled: [AccApplicationStatusId.SeatOffered, AccApplicationStatusId.SeatAccepted, AccApplicationStatusId.InCohort].includes(status.accApplicationStatusId),
		}))
	});

	public readonly toAccApplicationStatusId = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Status',
			options: this.statusOptions(),
			isRequired: true,
			isSpanish: false,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly() && (!!this.fromAccApplicationStatusId() && ![AccApplicationStatusId.SeatAccepted, AccApplicationStatusId.InCohort].includes(this.fromAccApplicationStatusId()!))),
		}),
		actualValue: computed(() => this.fromAccApplicationStatusId() || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'statusId',
		form: this.form,
	}));

	public readonly note = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Note',
			min: 0,
			fixCase: 'sentence-case',
			isRequired: false,
			multiLine: true,
			isEnabled: computed(() => !this.accAppDialogDataService.isReadonly())
		}),
		actualValue: computed(() => this.logNotes() ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'note',
		form: this.form,
	}));


	/**
 * Determines if the save button should be disabled based on the current status.
 * If the current status is AcceptCommitted or AcceptedAndAssigned, the button is disabled.
 * If the toStatusField's pending value matches the fromStatusId, the button is also disabled.
 */
	public disableSaveBtn = computed(() => {

		if (this.accAppDialogDataService.isReadonly()) return true;

		const fromStatusId = this.fromAccApplicationStatusId();

		if ([AccApplicationStatusId.SeatAccepted, AccApplicationStatusId.InCohort].includes(fromStatusId!)) return true;

		const toStatusId = this.toAccApplicationStatusId().pendingValue() || this.toAccApplicationStatusId().actualValue();
		const applNote = this.note().pendingValue() || this.note().actualValue();
		if (fromStatusId == toStatusId
			&& this.accAppDialogDataService.dialogData()?.details.isDeferred == this.deferApplication()
			&& applNote == this.logNotes())
			return true;

		return false;

	});

	public onChangeDeferApplication(isChecked: boolean) {
		this.deferValue.set(isChecked);
	}

	/**
	 * Convert status options to DescriptiveSelectItem format for the dashboard widget
	 */
	public readonly descriptiveStatusItems = computed<DescriptiveSelectItem[]>(() => {
		const options = this.statusOptions();

		return options.map(option => {
			const iconClass = this.getStatusIcon(option.value);
			const iconColor = this.getIconColor(iconClass);

			return {
				uniqueId: option.value,
				text: option.text,
				subText: option.subText,
				disabled: option.disabled || false,
				iconClass: iconClass,
				iconColor: option.disabled ? '#999' : iconColor
			};
		});
	});

	/**
	 * Get the selected descriptive item for the dashboard widget
	 */
	public readonly selectedDescriptiveItem = computed<DescriptiveSelectItem | undefined>(() => {
		const currentValue = this.toAccApplicationStatusId().pendingValue() || this.toAccApplicationStatusId().actualValue();
		return this.descriptiveStatusItems().find(item => item.uniqueId === currentValue);
	});

	/**
	 * Check if the descriptive select field is enabled
	 */
	public readonly isDescriptiveSelectEnabled = computed(() => {
		const isEnabled = this.toAccApplicationStatusId().config().isEnabled;
		return typeof isEnabled === 'function' ? isEnabled() : isEnabled;
	});

	/**
	 * Get the custom icon for the current selected status
	 */
	public readonly currentStatusIcon = computed<string>(() => {
		const selectedItem = this.selectedDescriptiveItem();
		return selectedItem?.iconClass || '';
	});

	/**
	 * Handle selection change from the descriptive select field
	 */
	public onDescriptiveSelectionChange(item: DescriptiveSelectItem | undefined) {
		if (item?.uniqueId !== undefined && typeof item.uniqueId === 'number') {
			// Update the pending value - this will sync with the hidden common-select-field
			this.toAccApplicationStatusId().pendingValue.set(item.uniqueId);
		}
	}

	public resetValues() {
		this.toAccApplicationStatusId().pendingValue.set(null);
		this.note().pendingValue.set(null);
	}
}